import {Avatar, Button, Group, Menu, Stack, Text, Tooltip, UnstyledButton} from '@mantine/core';
import {useMediaQuery} from '@mantine/hooks';
import {
    IconChevronDown,
    IconKey,
    IconLock,
    IconLogout,
} from '@tabler/icons-react';
import {useQuery} from '@tanstack/react-query';
import {jwtDecode} from 'jwt-decode';
import React, {useMemo, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {PasskeyService} from '../../client';
import {useAuthStore} from '../../store/authStore';
import {formatDisplayUsername} from '../../utils/formatDisplayUsername';
import type {UserRole} from '../../utils/roles';
import {ChangePasswordModal} from '../users/ChangePasswordModal';
import {ThemeSwitcher} from '../themeSwitcher/ThemeSwitcher';

type UserAccountMenuProps = {
    variant?: 'header' | 'sidebar';
    collapsed?: boolean;
    initials?: string;
    role?: UserRole;
    roleLabel?: string;
};

export function UserAccountMenu({
    variant = 'header',
    collapsed = false,
    initials = '?',
    roleLabel = '',
}: UserAccountMenuProps) {
    const token = useAuthStore((state) => state.token);
    const logout = useAuthStore((state) => state.logout);
    const navigate = useNavigate();
    const [passwordModalOpen, setPasswordModalOpen] = useState(false);

    const username = useMemo(() => {
        if (!token) {
            return '';
        }
        try {
            return jwtDecode<{sub?: string}>(token).sub ?? '';
        } catch {
            return '';
        }
    }, [token]);

    const {data: passkeyStatus} = useQuery({
        queryKey: ['passkey-status'],
        queryFn: PasskeyService.passkeyStatusApiV1PasskeyStatusGet,
    });

    const isMobile = useMediaQuery('(max-width: 48em)');
    const hasPasskey = passkeyStatus?.has_passkey ?? false;
    const displayName = username ? formatDisplayUsername(username) : 'Account';
    const tooltipLabel = roleLabel ? `${displayName} (${roleLabel})` : displayName;
    const menuPosition = variant === 'sidebar'
        ? (isMobile ? 'top-start' : 'right-start')
        : 'bottom-end';
    const menuWithinPortal = variant === 'sidebar' && isMobile;

    const menuTarget = variant === 'sidebar' ? (
        collapsed ? (
            <Menu.Target>
                <Tooltip label={tooltipLabel} position="right" withArrow>
                    <UnstyledButton aria-label={displayName}>
                        <Avatar radius="md" size="md" color="orange">{initials}</Avatar>
                    </UnstyledButton>
                </Tooltip>
            </Menu.Target>
        ) : (
            <Menu.Target>
                <Button
                    variant="subtle"
                    fullWidth
                    justify="flex-start"
                    leftSection={<Avatar radius="md" size="sm" color="orange">{initials}</Avatar>}
                    rightSection={<IconChevronDown size={14} stroke={1.5}/>}
                    styles={{inner: {justifyContent: 'flex-start'}}}
                >
                    <Stack gap={0} align="flex-start" style={{overflow: 'hidden', minWidth: 0}}>
                        <Text size="sm" truncate>{displayName}</Text>
                        {roleLabel ? (
                            <Text size="xs" c="dimmed" truncate>{roleLabel}</Text>
                        ) : null}
                    </Stack>
                </Button>
            </Menu.Target>
        )
    ) : (
        <Menu.Target>
            <Button
                variant="light"
                size="compact-sm"
                radius="md"
                rightSection={<IconChevronDown size={14} stroke={1.5}/>}
            >
                {displayName}
            </Button>
        </Menu.Target>
    );

    return (
        <>
            <Menu position={menuPosition} withinPortal={menuWithinPortal}>
                {menuTarget}
                <Menu.Dropdown>
                    <Menu.Item
                        leftSection={<IconKey size={16}/>}
                        onClick={() => navigate('/passkey')}
                    >
                        Manage Passkey
                    </Menu.Item>
                    <Menu.Item
                        leftSection={<IconLock size={16}/>}
                        disabled={hasPasskey}
                        onClick={() => setPasswordModalOpen(true)}
                    >
                        {hasPasskey ? 'Change password (Passkey active)' : 'Change password'}
                    </Menu.Item>
                    <Menu.Divider/>
                    <Menu.Item
                        leftSection={<IconLogout size={16}/>}
                        color="red"
                        onClick={() => logout()}
                    >
                        Logout
                    </Menu.Item>
                    <Menu.Divider/>
                    <Menu.Item closeMenuOnClick={false}>
                        <Group justify="space-between" wrap="nowrap">
                            <Text size="sm">Theme</Text>
                            <ThemeSwitcher/>
                        </Group>
                    </Menu.Item>
                </Menu.Dropdown>
            </Menu>
            {username ? (
                <ChangePasswordModal
                    opened={passwordModalOpen}
                    onClose={() => setPasswordModalOpen(false)}
                    username={username}
                />
            ) : null}
        </>
    );
}
